import type { Aviso, MetaTiendaVista, TotalesCaptura } from '../types/metas.types';
import { pctConSigno, pesosConSigno, pesosEnteros } from './formato';
import { metaTiendaComparable } from './totales';

/**
 * Los cuatro indicadores de la Captura (diseño `Main`): META DE TIENDA, SUMA
 * CAPTURADA, POR ASIGNAR y COLABORADORES LISTOS. Se recalculan en cada tecla
 * con los totales EN VIVO (`totalesCaptura`), sin ir al servidor.
 */
export type TonoIndicador = 'normal' | 'exito' | 'error' | 'aviso';

export interface Indicador {
  valor: string;
  nota: string;
  tono: TonoIndicador;
}

/**
 * La meta de tienda y por qué no se compara cuando no se puede: sólo cuenta
 * con los 7 días y SIN IVA (`metaTiendaComparable`). Una meta con IVA se marca,
 * no se convierte (decisión del 29-sep).
 */
export function indicadorMetaTienda(m: MetaTiendaVista | null | undefined): Indicador & { comparable: number | null } {
  const comparable = metaTiendaComparable(m);
  if (comparable !== null) return { valor: pesosEnteros(comparable), nota: 'Semana completa, sin IVA', tono: 'normal', comparable };
  if (m?.conIva)
    return {
      valor: m.monto !== null && m.monto > 0 ? pesosEnteros(m.monto) : '—',
      nota: 'Llegó con IVA: no se compara',
      tono: 'aviso',
      comparable,
    };
  if (m && m.dias > 0 && m.dias < 7)
    return {
      valor: m.monto !== null ? pesosEnteros(m.monto) : '—',
      nota: `Sólo ${m.dias} de 7 días cargados`,
      tono: 'aviso',
      comparable,
    };
  return { valor: 'Sin meta', nota: 'No hay meta de tienda esta semana', tono: 'normal', comparable };
}

/** Hay meta de tienda, pero no los 7 días (y ninguno con IVA): no se compara. */
const esIncompleta = (m: MetaTiendaVista | null | undefined): m is MetaTiendaVista =>
  !!m && !m.conIva && m.dias > 0 && m.dias < 7;

/**
 * «Meta de tienda incompleta: 6 de 7 días cargados» (revisión de la fase B,
 * 21-oct-2026): el recuadro decía «$274,624 · Sólo 6 de 7 días cargados» y, en
 * la misma pantalla, el aviso «No hay meta de tienda cargada» y el panel del
 * regional «sin meta de tienda». Este texto es el que usan el aviso y el panel.
 * `null` si no está incompleta (completa, sin ningún día o con IVA).
 */
export function textoMetaIncompleta(m: MetaTiendaVista | null | undefined): string | null {
  return esIncompleta(m) ? `Meta de tienda incompleta: ${m.dias} de 7 días cargados` : null;
}

/** Por qué POR ASIGNAR no compara, cuando no es «no hay meta de tienda». */
export function notaSinComparar(m: MetaTiendaVista | null | undefined): string | undefined {
  if (metaTiendaComparable(m) !== null) return undefined;
  if (m?.conIva) return 'Meta de tienda con IVA: la suma no se compara';
  if (esIncompleta(m)) return 'Meta de tienda incompleta: la suma no se compara';
  return undefined;
}

/**
 * Los avisos de la API con la meta de tienda que sí se conoce: con la meta
 * incompleta, «No hay meta de tienda cargada para esta semana» (`SIN_META_TIENDA`,
 * la API no distingue) pasa a decir cuántos días hay. Lo demás, igual.
 */
export function avisosConMetaTienda(avisos: Aviso[], m: MetaTiendaVista | null | undefined): Aviso[] {
  const incompleta = textoMetaIncompleta(m);
  if (!incompleta) return avisos;
  return avisos.map((a) => (a.codigo === 'SIN_META_TIENDA' ? { ...a, mensaje: `${incompleta}; no se puede comparar la suma.` } : a));
}

/**
 * POR ASIGNAR: lo que falta repartir contra la meta de tienda. Se escribe como
 * en el diseño —«−$37,921» en rojo cuando falta, «+$1,200» cuando la suma se
 * pasa— y la nota dice si queda dentro de la tolerancia del regional. Sin meta
 * comparable, `sinComparar` dice por qué (incompleta, con IVA); si no se da,
 * es que no hay meta de tienda.
 */
export function indicadorPorAsignar(suma: number, metaTienda: number | null, tolerancia = 0.02, sinComparar?: string): Indicador {
  if (metaTienda === null) return { valor: '—', nota: sinComparar ?? 'Sin meta de tienda contra qué comparar', tono: 'normal' };
  const diferencia = Math.round(suma - metaTienda);
  const pct = metaTienda > 0 ? (suma - metaTienda) / metaTienda : null;
  const dentro = pct !== null && Math.abs(pct) <= tolerancia;
  const tolerado = `Dentro de la tolerancia (±${Math.round(tolerancia * 1000) / 10} %)`;
  if (diferencia === 0) return { valor: '$0', nota: 'Cuadra con la meta de tienda', tono: 'exito' };
  if (diferencia < 0)
    return { valor: pesosConSigno(diferencia), nota: dentro ? tolerado : 'Falta repartir contra la meta de tienda', tono: 'error' };
  return {
    valor: pesosConSigno(diferencia),
    nota: dentro ? tolerado : `La suma pasa la meta de tienda (${pctConSigno(pct)})`,
    tono: 'normal',
  };
}

/**
 * COLABORADORES LISTOS «N de M» — definición ÚNICA, la misma en la app de la
 * tienda y en el admin (revisión de la fase B, 21-oct-2026; la app decía
 * «10 de 10» y el admin «5 de 5» para la misma tienda): de las personas cuyo
 * puesto OBLIGA a llevar meta (M), cuántas tienen meta o motivo (N). Las
 * opcionales (gerente, subgerente) y las que no llevan meta por su puesto NO
 * entran en el «de M», tengan meta o no; la nota las cuenta aparte:
 * «Todos listos · 4 sin meta por su puesto · 2 opcionales».
 *
 * Con totales sin el desglose (`obligatorias`, que sólo calcula el navegador),
 * se cae a la cuenta anterior (con meta o motivo + pendientes).
 */
export function indicadorListos(t: TotalesCaptura): { listos: number; de: number; pendientes: number; nota: string; tono: TonoIndicador } {
  const de = t.obligatorias ?? t.conMeta + t.sinMeta + t.pendientes;
  const listos = Math.max(0, de - t.pendientes);
  if (t.pendientes > 0) return { listos, de, pendientes: t.pendientes, nota: `${t.pendientes} sin meta ni motivo`, tono: 'aviso' };
  const desglose = textoDesglosePuesto(t.obligatorias !== undefined ? t.noLleva ?? 0 : t.sinMetaPorPuesto ?? 0, t.opcionales ?? 0);
  return {
    listos,
    de,
    pendientes: 0,
    nota: desglose ? `Todos listos · ${desglose}` : 'Todos tienen meta o motivo',
    tono: 'exito',
  };
}

/** «4 sin meta por su puesto · 2 opcionales» (sin las partes en cero); `''` si no hay nada que decir. */
export function textoDesglosePuesto(sinMetaPorPuesto: number, opcionales: number): string {
  return [
    sinMetaPorPuesto > 0 ? `${sinMetaPorPuesto} sin meta por su puesto` : null,
    opcionales > 0 ? `${opcionales} ${opcionales === 1 ? 'opcional' : 'opcionales'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
